/**
 * Flinks Integration Utilities
 *
 * This module provides utilities for Flinks iframe URL generation across different projects.
 * It handles company-specific branding (Iceberg vs iFinance) and environment configurations.
 */
/**
 * Generates a Flinks iframe URL with the specified parameters
 *
 * @param params Configuration object for iframe URL generation
 * @returns A complete Flinks iframe URL with all required parameters
 *
 * @example
 * ```typescript
 * const iframeUrl = generateFlinksIframeUrl({
 *   baseUrl: import.meta.env.VITE_FLINKS_IFRAME,
 *   customerName: 'iFinance', // or 'Iceberg'
 *   isDemoMode: getEnvRuntimeVariable<boolean>(import.meta.env.VITE_USE_DEMO_FLINKS),
 *   envTag: import.meta.env.VITE_ENV,
 *   externalServiceId,
 *   language: i18n.language,
 *   cleanWhitespace: true
 * })
 * ```
 */
export function generateFlinksIframeUrl(params) {
    const { baseUrl, customerName, isDemoMode, envTag, externalServiceId, language, cleanWhitespace = false, institutionShortName } = params;
    // When an institution is pre-selected, deep-link into its credential screen and disable
    // the in-iframe institution filter and consent (consent is captured on our own page).
    const isDirectInstitution = Boolean(institutionShortName);
    const path = isDirectInstitution ? `${baseUrl}/Credential/${institutionShortName}` : baseUrl;
    let url = path +
        `?theme=light
  &desktopLayout=true
  &customerName=${customerName}
  &institutionFilterEnable=${isDirectInstitution ? 'false' : 'true'}
  &demo=${isDemoMode ? 'true' : 'false'}
  &consentEnable=${isDirectInstitution ? 'false' : 'true'}
  &accountSelectorEnable=true
  &daysOfTransactions=Days365
  &monthsOfStatements=Months12
  &staticLoadingEnable=true
  &tag=${envTag}_${externalServiceId}
  &language=${language}
  &scheduleRefresh=false`;
    if (cleanWhitespace) {
        url = url
            .replace(/ /g, '')
            .replace(/(\r\n|\n|\r)/gm, '');
    }
    return url;
}
